import { useLingui } from '@lingui/react'
import {
  AppShell,
  Box,
  Container,
  Group,
  Image,
  NavLink,
  rem,
  Stack,
  useMantineTheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import faceImage from '../assets/images/face.png'
import markkuLogo from '../assets/images/markku_fi.png'
import { BottomTabNavigator } from '../components/BottomTabNavigator'
import {
  CompanyPortalBottomNav,
  CompanyPortalSidebar,
  getCompanyIdFromPath,
  getCompanyPortalActiveTab,
  isCompanyPortalMainRoute,
} from '../components/company-portal/CompanyPortalNav'
import { ImpersonationBanner } from '../components/ImpersonationBanner'
import {
  getNavigationItems,
  NavigationTab,
  useUnreadMessageCount,
} from '../config/navigation'
import { useAuthStore } from '../store/useAuthStore'

function AppLayout() {
  const { i18n } = useLingui()
  const location = useLocation()
  const navigate = useNavigate()
  const theme = useMantineTheme()
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`)

  // Company portal detection
  const isCompanyPortal = location.pathname.startsWith('/company-portal')
  const companyId = isCompanyPortal
    ? getCompanyIdFromPath(location.pathname)
    : null
  const portalActiveTab = isCompanyPortal
    ? getCompanyPortalActiveTab(location.pathname)
    : null
  const isPortalMainRoute =
    isCompanyPortal && isCompanyPortalMainRoute(location.pathname)

  // Use the robust logic: show nav only on top-level routes within this layout.
  // A path like "/home" splits into ["", "home"] (length 2).
  // A path like "/visits/123" splits into ["", "visits", "123"] (length 3).
  const isMainRoute = location.pathname.split('/').length <= 2

  // Determine active tab from the first path segment
  const pathSegment = location.pathname.split('/')[1] || 'home'

  // Map the route to the correct tab ID
  const getActiveTab = () => {
    // maintenance-log is still under the maintenance tab
    if (pathSegment === 'maintenance-log') return 'maintenance'
    return pathSegment
  }

  const activeTab = getActiveTab() as NavigationTab

  const unreadCount = useUnreadMessageCount()
  const navItems = getNavigationItems(20, unreadCount)

  const showSidebar = isDesktop

  return (
    <AppShell
      navbar={{
        width: 250,
        breakpoint: 'md',
        collapsed: { mobile: true, desktop: !showSidebar },
      }}
      padding={0}
      styles={{
        main: {
          paddingRight: 0,
          paddingTop: 0,
          paddingBottom: 0,
        },
      }}
    >
      {/* Desktop Sidebar Navigation */}
      <AppShell.Navbar p="md" visibleFrom="md">
        <Stack gap="md">
          {/* Logo/Branding */}
          <Link to={isCompanyPortal ? '/company-portal' : '/home'}>
            <Group gap="md" px="xs" wrap="nowrap">
              <Image
                src={faceImage}
                alt="Markku"
                style={{
                  width: rem(40),
                  height: rem(40),
                  objectFit: 'contain',
                }}
                fit="contain"
              />
              <Image
                src={markkuLogo}
                alt="Markku"
                style={{
                  height: rem(24),
                  objectFit: 'contain',
                  objectPosition: 'left',
                }}
                fit="contain"
              />
            </Group>
          </Link>

          {/* Navigation Links */}
          {isCompanyPortal ? (
            companyId ? (
              <CompanyPortalSidebar
                companyId={companyId}
                activeTab={portalActiveTab!}
              />
            ) : null
          ) : (
            <Stack gap={4}>
              {navItems.map((item) => (
                <NavLink
                  key={item.id}
                  label={i18n._(item.label)}
                  leftSection={item.icon}
                  active={activeTab === item.id}
                  onClick={() => navigate({ to: item.route })}
                  rightSection={
                    item.badge ? (
                      <Box
                        style={{
                          backgroundColor: 'var(--mantine-color-red-6)',
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 11,
                          fontWeight: 600,
                        }}
                      >
                        {item.badge}
                      </Box>
                    ) : undefined
                  }
                />
              ))}
            </Stack>
          )}
        </Stack>
      </AppShell.Navbar>

      {/* Main Content Area */}
      <AppShell.Main>
        {isDesktop ? (
          /* Desktop Layout - Natural page scrolling */
          <Container size="lg" px={0}>
            <ImpersonationBanner />
            <Outlet />
          </Container>
        ) : (
          /* Mobile Layout - Fixed bottom navigation */
          <Box
            style={{
              height: '100dvh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ImpersonationBanner />
            <Box
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
              }}
            >
              <Outlet />
            </Box>

            {isCompanyPortal && companyId && isPortalMainRoute && (
              <CompanyPortalBottomNav
                companyId={companyId}
                activeTab={portalActiveTab!}
              />
            )}

            {!isCompanyPortal && isMainRoute && (
              <BottomTabNavigator activeTab={activeTab} />
            )}
          </Box>
        )}
      </AppShell.Main>
    </AppShell>
  )
}

// Export the component as the Route
export const Route = createFileRoute('/_app')({
  beforeLoad: ({ location }) => {
    const isAuthenticated = useAuthStore.getState().isAuthenticated
    if (!isAuthenticated) {
      throw redirect({
        to: '/',
        search: { redirect: location.pathname },
      })
    }
  },
  component: AppLayout,
})
